import * as THREE from 'three';
import { hash3 } from './rng.js';

// ---------------------------------------------------------------------------
// Palette: block id -> appearance. Id 0 is always air.
// ---------------------------------------------------------------------------

export class Palette {
    constructor() {
        this.colors = [new THREE.Color(0, 0, 0)];
        this.emissive = [0];
        this.jitter = [0];
        this.byKey = new Map();
    }

    /**
     * Register a block type.
     * @param {string} key    name, so lookups read like `pal.id('grass')`
     * @param {number} hex    base colour
     * @param {object} opts   { emissive: 0..1 glow at night, jitter: 0..1 per-voxel tint noise }
     */
    add(key, hex, { emissive = 0, jitter = 0.05 } = {}) {
        if (this.byKey.has(key)) return this.byKey.get(key);
        const id = this.colors.length;
        if (id > 255) throw new Error('Palette overflow: voxel ids are Uint8');
        this.colors.push(new THREE.Color(hex).convertSRGBToLinear());
        this.emissive.push(emissive);
        this.jitter.push(jitter);
        this.byKey.set(key, id);
        return id;
    }

    id(key) {
        const v = this.byKey.get(key);
        if (v === undefined) throw new Error(`Unknown block "${key}"`);
        return v;
    }
}

// ---------------------------------------------------------------------------
// Face table. Corners are listed counter-clockwise seen from outside, so the
// winding gives us outward normals without any runtime cross products.
// ---------------------------------------------------------------------------

const FACES = [
    { n: [1, 0, 0], t: [1, 2], c: [[1, 0, 1], [1, 0, 0], [1, 1, 0], [1, 1, 1]] }, // +X
    { n: [-1, 0, 0], t: [1, 2], c: [[0, 0, 0], [0, 0, 1], [0, 1, 1], [0, 1, 0]] }, // -X
    { n: [0, 1, 0], t: [0, 2], c: [[0, 1, 1], [1, 1, 1], [1, 1, 0], [0, 1, 0]] }, // +Y
    { n: [0, -1, 0], t: [0, 2], c: [[0, 0, 0], [1, 0, 0], [1, 0, 1], [0, 0, 1]] }, // -Y
    { n: [0, 0, 1], t: [0, 1], c: [[0, 0, 1], [1, 0, 1], [1, 1, 1], [0, 1, 1]] }, // +Z
    { n: [0, 0, -1], t: [0, 1], c: [[1, 0, 0], [0, 0, 0], [0, 1, 0], [1, 1, 0]] }, // -Z
];

// Classic voxel corner AO: 0 (fully occluded) .. 3 (open).
function cornerAO(side1, side2, corner) {
    if (side1 && side2) return 0;
    return 3 - (side1 + side2 + corner);
}

const AO_LEVELS = [0.42, 0.62, 0.82, 1.0];

// ---------------------------------------------------------------------------

export class VoxelGrid {
    constructor(w, h, d) {
        this.w = w;
        this.h = h;
        this.d = d;
        this.data = new Uint8Array(w * h * d);
    }

    idx(x, y, z) {
        return (y * this.d + z) * this.w + x;
    }

    inside(x, y, z) {
        return x >= 0 && y >= 0 && z >= 0 && x < this.w && y < this.h && z < this.d;
    }

    get(x, y, z) {
        if (!this.inside(x, y, z)) return 0;
        return this.data[this.idx(x, y, z)];
    }

    set(x, y, z, id) {
        if (!this.inside(x, y, z)) return;
        this.data[this.idx(x, y, z)] = id;
    }

    /** Set only if the target cell is currently air. */
    setIfEmpty(x, y, z, id) {
        if (!this.inside(x, y, z)) return;
        const i = this.idx(x, y, z);
        if (this.data[i] === 0) this.data[i] = id;
    }

    /** Axis-aligned filled box, inclusive of both corners. */
    box(x0, y0, z0, x1, y1, z1, id) {
        const [ax, bx] = x0 <= x1 ? [x0, x1] : [x1, x0];
        const [ay, by] = y0 <= y1 ? [y0, y1] : [y1, y0];
        const [az, bz] = z0 <= z1 ? [z0, z1] : [z1, z0];
        for (let y = ay; y <= by; y++) {
            for (let z = az; z <= bz; z++) {
                for (let x = ax; x <= bx; x++) this.set(x, y, z, id);
            }
        }
    }

    /** Solid vertical cylinder, handy for mill bodies and tree trunks. */
    cylinder(cx, cz, y0, y1, radius, id, { taper = 0 } = {}) {
        for (let y = y0; y <= y1; y++) {
            const t = y1 === y0 ? 0 : (y - y0) / (y1 - y0);
            const r = radius * (1 - taper * t);
            const ri = Math.ceil(r);
            for (let z = -ri; z <= ri; z++) {
                for (let x = -ri; x <= ri; x++) {
                    if (x * x + z * z <= r * r) this.set(cx + x, y, cz + z, id);
                }
            }
        }
    }

    /** Hollow ring of one voxel thickness — balconies, rims, fences. */
    ring(cx, cz, y, radius, id, { thickness = 1 } = {}) {
        const ro = radius;
        const ri = radius - thickness;
        const b = Math.ceil(ro);
        for (let z = -b; z <= b; z++) {
            for (let x = -b; x <= b; x++) {
                const dsq = x * x + z * z;
                if (dsq <= ro * ro && dsq > ri * ri) this.set(cx + x, y, cz + z, id);
            }
        }
    }

    /** Bounding box of non-air voxels, or null for an empty grid. */
    bounds() {
        let minX = Infinity, minY = Infinity, minZ = Infinity;
        let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
        for (let y = 0; y < this.h; y++) {
            for (let z = 0; z < this.d; z++) {
                for (let x = 0; x < this.w; x++) {
                    if (this.data[this.idx(x, y, z)] === 0) continue;
                    if (x < minX) minX = x;
                    if (y < minY) minY = y;
                    if (z < minZ) minZ = z;
                    if (x > maxX) maxX = x;
                    if (y > maxY) maxY = y;
                    if (z > maxZ) maxZ = z;
                }
            }
        }
        return minX === Infinity ? null : { minX, minY, minZ, maxX, maxY, maxZ };
    }

    /**
     * Turn the grid into a single BufferGeometry.
     *
     * Only faces touching air are emitted, and each vertex carries a baked
     * ambient-occlusion term computed from its three diagonal neighbours —
     * the effect that makes voxel scenes look carved rather than stacked.
     *
     * @param {Palette} palette
     * @param {object} opts  { voxelSize, origin: THREE.Vector3, cullBelow }
     */
    build(palette, { voxelSize = 1, origin = new THREE.Vector3(), cullBelow = -1 } = {}) {
        const positions = [];
        const normals = [];
        const colors = [];
        const extras = []; // x: ao, y: emissive

        const tmp = new THREE.Color();

        for (let y = 0; y < this.h; y++) {
            if (y < cullBelow) continue;
            for (let z = 0; z < this.d; z++) {
                for (let x = 0; x < this.w; x++) {
                    const id = this.data[this.idx(x, y, z)];
                    if (id === 0) continue;

                    const base = palette.colors[id];
                    const jitterAmt = palette.jitter[id];
                    const emissive = palette.emissive[id];
                    const shade = 1 + (hash3(x, y, z) - 0.5) * 2 * jitterAmt;
                    tmp.copy(base).multiplyScalar(shade);

                    for (let f = 0; f < 6; f++) {
                        const face = FACES[f];
                        const nx = face.n[0], ny = face.n[1], nz = face.n[2];
                        if (this.get(x + nx, y + ny, z + nz) !== 0) continue;

                        const [ta, tb] = face.t;
                        const ao = [0, 0, 0, 0];

                        for (let ci = 0; ci < 4; ci++) {
                            const c = face.c[ci];
                            // Step +/-1 along each tangent axis, offset onto the
                            // air side of the face.
                            const du = [0, 0, 0];
                            const dv = [0, 0, 0];
                            du[ta] = c[ta] * 2 - 1;
                            dv[tb] = c[tb] * 2 - 1;

                            const s1 = this.get(x + nx + du[0], y + ny + du[1], z + nz + du[2]) !== 0 ? 1 : 0;
                            const s2 = this.get(x + nx + dv[0], y + ny + dv[1], z + nz + dv[2]) !== 0 ? 1 : 0;
                            const co = this.get(
                                x + nx + du[0] + dv[0],
                                y + ny + du[1] + dv[1],
                                z + nz + du[2] + dv[2],
                            ) !== 0 ? 1 : 0;

                            ao[ci] = cornerAO(s1, s2, co);
                        }

                        // Split the quad along the darker diagonal, otherwise the
                        // AO gradient warps depending on triangulation.
                        const flip = ao[0] + ao[2] > ao[1] + ao[3];
                        const order = flip ? [0, 1, 2, 0, 2, 3] : [1, 2, 3, 1, 3, 0];

                        for (let k = 0; k < 6; k++) {
                            const ci = order[k];
                            const c = face.c[ci];
                            positions.push(
                                origin.x + (x + c[0]) * voxelSize,
                                origin.y + (y + c[1]) * voxelSize,
                                origin.z + (z + c[2]) * voxelSize,
                            );
                            normals.push(nx, ny, nz);
                            colors.push(tmp.r, tmp.g, tmp.b);
                            extras.push(AO_LEVELS[ao[ci]], emissive);
                        }
                    }
                }
            }
        }

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
        geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
        geometry.setAttribute('aExtra', new THREE.Float32BufferAttribute(extras, 2));
        geometry.computeBoundingSphere();
        geometry.computeBoundingBox();
        return geometry;
    }
}
